import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { RefreshActions } from '../state/refresh'
import { useRequests } from '../requests/useRequests'

interface ProductFormData {
  name: string
  code?: string
  description: string
  image: string
  category: string
  fileFormats: string[]
  brandId: number
  priority: number
  url?: string
  files?: string[]
}

export const useProductForm = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const requests = useRequests()

  // Pobieramy brandId z URL params lub z query string
  const urlBrandId = useParams<{ brandId: string }>().brandId
  const queryBrandId = searchParams.get('brandId')
  const brandId = urlBrandId || queryBrandId

  const isEditMode = Boolean(id)

  // Stany formularza
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    code: '',
    description: '',
    image: '',
    category: '',
    fileFormats: ['DWG'],
    brandId: 0,
    priority: 0,
    files: [],
    url: ''
  })

  const [loading, setLoading] = useState<boolean>(false)
  const [brands, setBrands] = useState<Array<{ id: number, name: string }>>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [files, setFiles] = useState<File[]>([])
  const [filesUploading, setFilesUploading] = useState(false)
  const [filesPreviews, setFilesPreviews] = useState<string[]>([])

  // Kategorie do wyboru
  const [mainCategories, setMainCategories] = useState<string[]>([
    'Meble', 'Oświetlenie', 'Elementy stałe', 'Urządzenia',
    'Mała architektura', 'Konstrukcje', 'Dekoracje i Dodatki'
  ])

  const [subCategories, setSubCategories] = useState<Record<string, string[]>>({
    Meble: [
      'Akcesoria meblowe',
      'Barki',
      'Biurka',
      'Blaty',
      'Bufety',
      'Fotele',
      'Hokery',
      'Komplety wypoczynkowe',
      'Kontenery',
      'Kredensy i Komody',
      'Krzesła',
      'Lady',
      'Ławy i ławki',
      'Łóżeczka',
      'Łóżka',
      'Meble łazienkowe',
      'Meble specjalne',
      'Nadstawki',
      'Narożniki',
      'Panele',
      'Podstawki',
      'Półki',
      'Profile meblowe',
      'Pufy',
      'Regały i Biblioteczki',
      'Skrzynie',
      'Sofy i Kanapy',
      'Stoły i Stoliki',
      'Szafki',
      'Szafy i Garderoby',
      'Szuflady',
      'Tablice',
      'Taborety',
      'Tkaniny i tekstylia',
      'Toaletki',
      'Wieszaki i Stojaki',
      'Witryny',
      'Zestawy'
    ],
    Oświetlenie: [
      'Akcesoria',
      'Kinkiety',
      'Lampy do szynoprzewodów',
      'Lampy natynkowe',
      'Lampy ogrodowe',
      'Lampy stojące',
      'Lampy wpuszczane',
      'Lampy zwieszane',
      'Latarnie',
      'Profile świetlne',
      'Reflektory',
      'Systemy oświetleniowe',
      'Szynoprzewody',
      'Źródła światła'
    ],
    'Elementy stałe': [
      'Akcesoria',
      'Baterie',
      'Drzwi',
      'Specjalistyczne',
      'Grzejniki',
      'Kabiny i Brodziki',
      'Kominki',
      'Natryski',
      'Okna',
      'Płytki i Kamienie',
      'Podłogi i Ściany',
      'Schody',
      'Umywalki',
      'Wanny',
      'WC',
      'Zlewy i Zlewozmywaki'
    ],
    Urządzenia: [
      'AGD',
      'AGD do zabudowy',
      'AGD małe',
      'Urządzenia grzewcze',
      'Wentylacja i Klimatyzacja'
    ],
    Konstrukcje: [
      'Akcesoria',
      'Altany ogrodowe',
      'Meble ogrodowe: zestawy kawowe',
      'Bramy',
      'Balie ogrodowe',
      'Ogrodzenia',
      'Profile',
      'Przegrody pionowe',
      'System zadaszeń',
      'Systemy modułowe',
      'Wiaty',
      'Zestawy kawowe',
      'Fotele techorattanowe',
      'Fotele bujane',
      'Leżaki do ogrodu',
      'Sauny ogrodowe'
    ],
    'Dekoracje i Dodatki': [
      'Akcesoria kuchenne',
      'Akcesoria łazienkowe',
      'Dekoracje drobne',
      'Dekoracje wiszące',
      'Donice, Doniczki i Osłonki',
      'Figurki dekoracyjne',
      'Koszyki i Pudełka',
      'Lampiony i Świeczniki',
      'Lustra',
      'Poduszki i Siedziska',
      'Ramki na zdjęcia',
      'Skrzynki drewniane',
      'Stojaki na książki i gazety',
      'Świece i Świeczki',
      'Tapety',
      'Tekstylia',
      'Wazony i Dzbany',
      'Wieszaki i stojaki',
      'Wycieraczki i Maty',
      'Zabudowy dekoracyjne',
      'Zielone panele'
    ],
    'Mała architektura': [
      'Stojaki na rowery',
      'Ławki parkowe i miejskie',
      'Kosze na śmieci miejskie, uliczne, parkowe',
      'Place Zabaw',
      'Skatepark',
      'Pumptrack',
      'Wakepark',
      'Wavepark',
      'Donice miejskie i parkowe',
      'Słupki miejskie, uliczne, parkowe',
      'Barierki',
      'Tablice ogłoszeniowe i informacyjne zewnętrzne',
      'Popielnice',
      'Leżaki miejskie, parkowe, betonowe',
      'Huśtawki miejskie',
      'Meble ogrodowo-piknikowe',
      'Płyty i kostki brukowe',
      'Wiata rowerowa',
      'Zewnętrzne stoły do gier',
      'Hamaki miejskie',
      'Grille parkowe, osiedlowe',
      'Przysiadki miejskie',
      'Pergole miejskie',
      'Siłownie miejskie',
      'Stacje do dezynfekcji'
    ]
  })

  const [thirdLevelCategories, setThirdLevelCategories] = useState<Record<string, Record<string, string[]>>>({
    'Elementy stałe': {
      Baterie: [
        'Akcesoria',
        'Akcesoria dla bidetów',
        'Baterie bidetowe',
        'Baterie kuchenne',
        'Baterie natryskowe',
        'Baterie specjalne',
        'Baterie umywalkowe',
        'Baterie wannowe',
        'Baterie wannowo-natryskowe',
        'Baterie wolnostojące',
        'Deszczownice',
        'Dysze natryskowe',
        'Systemy natryskowe podtynkowe',
        'Systemy prysznicowe natynkowe',
        'Wylewki'
      ],
      Drzwi: [
        'Akcesoria drzwiowe',
        'Drzwi antywłamaniowe',
        'Drzwi obrotowe',
        'Drzwi przesuwne',
        'Drzwi szklane',
        'Drzwi techniczne',
        'Drzwi wejściowe',
        'Drzwi wewnętrzne',
        'Drzwi zewnętrzne',
        'Ościeżnice'
      ],
      'Kabiny i Brodziki': [
        'Akcesoria',
        'Brodziki',
        'Drzwi prysznicowe',
        'Kabiny natryskowe',
        'Kabiny WC',
        'Parawany nawannowe',
        'Systemy bezdrzwiowe'
      ],
      Kominki: [
        'Akcesoria kominkowe',
        'Biokominki',
        'Kominki powietrzne',
        'Kominki z płaszczem wodnym',
        'Kratki kominkowe',
        'Piece wolnostojące',
        'Systemy kominowe',
        'Wkłady kominkowe'
      ],
      Natryski: [
        'Akcesoria prysznicowe',
        'Deszczownice',
        'Słuchawki natryskowe',
        'Systemy podtynkowe',
        'Węże natryskowe',
        'Zestawy natryskowe'
      ],
      Okna: [
        'Akcesoria okienne',
        'Okna dachowe',
        'Okna dwukwaterowe',
        'Okna niestandartowe',
        'Okna podwójne',
        'Okna potrójne',
        'Okna przesuwne',
        'Okna renowacyjne',
        'Okna rozwierne',
        'Okna standardowe',
        'Okna uniwersalne',
        'Parapety',
        'Rolety',
        'Systemy okienne',
        'Żaluzje'
      ],
      'Płytki i Kamienie': [
        'Elementy dekoracyjne i Dodatki',
        'Gresy',
        'Kafle piecowe',
        'Kamień elewacyjny i ścienny',
        'Kamień naturalny',
        'Klinkier',
        'Mozaiki',
        'Płytki podłogowe',
        'Płytki ścienne',
        'Spieki kwarcowe'
      ],
      'Podłogi i Ściany': [
        'Akcesoria',
        'Deski podłogowe',
        'Listwy dekoracyjne',
        'Panele akustyczne',
        'Panele podłogowe',
        'Panele ścienne',
        'Parkiety',
        'Posadzki',
        'Wykładziny akustyczne',
        'Wykładziny dywanowe',
        'Wykładziny elastyczne',
        'Wykładziny PVC'
      ],
      Schody: [
        'Akcesoria',
        'Deski na taras',
        'Deski podłogowe',
        'Listwy dekoracyjne',
        'Panele akustyczne',
        'Panele podłogowe',
        'Panele ścienne',
        'Parkiety',
        'Posadzki',
        'Wykładziny akustyczne',
        'Wykładziny dywanowe',
        'Wykładziny elastyczne',
        'Wykładziny PVC'
      ],
      Umywalki: [
        'Postumenty i Półpostumenty',
        'Umywalki blatowe',
        'Umywalki meblowe',
        'Umywalki nablatowe',
        'Umywalki podblatowe',
        'Umywalki półblatowe',
        'Umywalki wiszące',
        'Umywalki wolnostojące'
      ],
      Wanny: [
        'Akcesoria wannowe',
        'Wanny asymetryczne',
        'Wanny do zabudowy',
        'Wanny narożne',
        'Wanny nietypowe',
        'Wanny wolnostojące',
        'Wanny z hydromasażem'
      ],
      WC: [
        'Bidety',
        'WC stojące',
        'WC wiszące',
        'Pisuary'
      ],
      'Zlewy i Zlewozmywaki': [
        'Zlewy kwadratowe',
        'Zlewy nietypowe',
        'Zlewy okrągłe',
        'Zlewy owalne'
      ]
    },
    'Mała architektura': {
      'Stojaki na rowery': [
        'Stojaki rowerowe szeregowe',
        'Stojaki na rowery typu U',
        'Stojaki spiralne na rowery',
        'Stojaki rowerowe dwupoziomowe',
        'Stojaki rowerowe z reklamą',
        'Stojaki ekspozycyjne na rowery',
        'Stojaki na rowery dziecięce',
        'Stojaki ścienne na rowery',
        'Stojaki rowerowe ogumowane',
        'Stojaki rowerowe modułowe',
        'Podpórki dla rowerów',
        'Stojaki na hulajnogi',
        'Wieszaki rowerowe',
        'Stojaki rowerowe ciekawe kształty',
        'Stacje naprawy rowerów'
      ],
      'Ławki parkowe i miejskie': [
        'Ławki stalowe, metalowe, ocynkowane, lakierowane',
        'Ławki ze stali nierdzewnej',
        'Ławki żeliwne',
        'Ławki betonowe z betonu płukanego lub architektonicznego',
        'Ławki drewniane',
        'Ławki dworcowe, peronowe, do hali',
        'Ławki dwustronne – na perony, lotniska i do poczekalni',
        'Ławki łukowe i okrągłe',
        'Krzesła miejskie',
        'Ławki z deskami z tworzywa',
        'Ławki aluminiowe',
        'Ławki bez oparcia',
        'Ławki na murek',
        'Ławki na osiedle',
        'Ławki na plac zabaw',
        'Ławki na podwórko',
        'Ławki na skwer',
        'Ławki do ogrodu'
      ],
      'Kosze na śmieci miejskie, uliczne, parkowe': [
        'Kosze na śmieci stalowe, metalowe, ocynkowane, lakierowane',
        'Kosze na śmieci stalowo drewniane',
        'Kosze na śmieci ze stali nierdzewnej',
        'Kosze do segregacji śmieci',
        'Kosze betonowe na śmieci',
        'Kosze na śmieci żeliwne',
        'Kosze na psie odchody',
        'Kosze antyterrorystyczne, na dworce',
        'Kosze miejskie z listwami z kompozytu',
        'Kosze do ogrodu',
        'Kosze na plac zabaw'
      ],
      'Place Zabaw': [
        'Akcesoria',
        'Bujaki',
        'Domki',
        'Drabinki',
        'Gry',
        'Huśtawki',
        'Karuzele',
        'Kręciołki',
        'Lokomotywy',
        'Panele edukacyjne',
        'Piaskownice',
        'Równoważnie',
        'Slalomy',
        'Stoliki',
        'Ścianki wspinaczkowe',
        'Tablice',
        'Tyrolki',
        'Wagoniki',
        'Wieże',
        'Zestawy zabawowe',
        'Zjeżdżalnie'
      ],
      'Donice miejskie i parkowe': [
        'Donice stalowo drewniane',
        'Donice stalowe',
        'Donice ze stali nierdzewnej',
        'Donice betonowe',
        'Donice ogrodowe',
        'Gazony i Palisady',
        'Kraty i osłony pod drzewa',
        'Osłony pionowe do drzew',
        'Karmniki'
      ],
      'Słupki miejskie, uliczne, parkowe': [
        'Słupki uliczne stalowe i żeliwne'
      ],
      Barierki: [
        'Barierki trawnikowe'
      ],
      'Wiata rowerowa': [
        'Wiaty rowerowe na 5 stanowisk',
        'Wiaty rowerowe na 10 stanowisk',
        'Wiaty rowerowe na 15 stanowisk',
        'Wiaty rowerowe na 20 stanowisk',
        'Wiaty rowerowe na 25 stanowisk',
        'Wiaty rowerowe na 30 stanowisk',
        'Wiaty rowerowe dwustronne'
      ]
    }
  })

  const [selectedMainCategory, setSelectedMainCategory] = useState<string>('')
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('')
  const [selectedThirdCategory, setSelectedThirdCategory] = useState<string>('')

  // Formaty plików do wyboru
  const fileFormatOptions = [
    { id: '3DS', label: '3Ds Max (.3ds)' },
    { id: 'GSM', label: 'Archicad (.gsm)' },
    { id: 'DWG', label: 'Autocad (.dwg)' },
    { id: 'DXF', label: 'Autocad (.dxf)' },
    { id: 'OBJ', label: 'Object file 3D (.obj)' },
    { id: 'MTL', label: 'Object file 3D (.mtl)' },
    { id: 'SKP', label: 'SketchUp 3D (.skp)' },
    { id: 'ACO', label: 'ArCon (.aco)' },
    { id: 'DAE', label: 'Collada (.dae)' },
    { id: 'FBX', label: 'FBX (.fbx)' },
    { id: 'IWO', label: 'LigthWave 3D (.iwo)' },
    { id: '3DM', label: 'Rhinoceros (.3dm)' },
    { id: 'WRL', label: 'Solidworks (.wrl)' },
    { id: 'STL', label: 'STL (.stl)' },
    { id: 'JPG', label: 'JPG' },
    { id: 'PNG', label: 'PNG' },
    { id: 'TEXTURES', label: 'Tekstury' },
    { id: 'MATERIALS', label: 'Materiały' }
  ]

  // Toast notifications
  const showToast = (message: string) => {
    dispatch(
      RefreshActions.setToastMessage({
        message
      })
    )
  }

  // Pobieranie danych
  useEffect(() => {
    // Weryfikacja tokenu JWT
    const token = localStorage.getItem('Kreatlo-jwtToken')
    if (!token) {
      showToast('Nie jesteś zalogowany. Zaloguj się ponownie.')
      navigate('/login')
      return
    }

    setLoading(true)

    // Pobranie listy marek
    requests.getBrands({ limit: 100 })
      .then(response => {
        if (response.data && response.data.data) {
          setBrands(response.data.data)
        }
      })
      .catch(error => {
        console.error('Błąd podczas pobierania listy marek:', error)
        showToast('Nie udało się pobrać listy marek')
      })

    // Pobranie danych produktu w trybie edycji
    if (isEditMode && id) {
      requests.getProduct(parseInt(id))
        .then(response => {
          const productData = response.data
          setFormData({
            name: productData.name || '',
            code: productData.code || '',
            description: productData.description || '',
            image: productData.image || '',
            category: productData.category || '',
            fileFormats: productData.fileFormats || ['DWG'],
            brandId: productData.brandId || (brandId ? parseInt(brandId, 10) : 0),
            priority: productData.priority || 0,
            files: productData.files || [],
            url: productData.url || ''
          })

          // Pobierz presigned URL dla obrazu
          if (productData.image) {
            requests.getFileUrl(productData.image)
              .then(response => {
                setPreviewUrl(response.data.presignedUrl)
              })
              .catch(error => {
                console.error('Błąd podczas pobierania URL obrazu:', error)
              })
          }

          setFiles(productData.files?.map((f: string) => ({
            type: f.split('.')[1],
            name: f
          })) || [])

          // Ustawienie wybranych kategorii
          setSelectedCategories(productData.category || '')

          setLoading(false)
        })
        .catch(error => {
          console.error('Błąd podczas pobierania danych produktu:', error)
          showToast('Nie udało się pobrać danych produktu')
          setLoading(false)
        })
    } else {
      // Dla nowego produktu
      if (brandId) {
        setFormData(prev => ({
          ...prev,
          brandId: parseInt(brandId, 10),
          files: []
        }))
      }
      setLoading(false)
    }
  }, [id, brandId, isEditMode, navigate, dispatch])

  // Metoda ustawiająca kategorie na podstawie ścieżki
  const setSelectedCategories = (categoryPath: string) => {
    if (!categoryPath) return

    const parts = categoryPath.split('/')
    if (parts.length > 0) {
      setSelectedMainCategory(parts[0])
      if (parts.length > 1) {
        setSelectedSubCategory(parts[1])
        if (parts.length > 2) {
          setSelectedThirdCategory(parts[2])
        }
      }
    }
  }

  // Obsługa zmian formularza
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    if (name === 'priority') {
      const priorityValue = parseInt(value, 10) || 0
      setFormData(prev => ({ ...prev, [name]: priorityValue }))
    } else if (name === 'brandId') {
      const brandIdValue = parseInt(value, 10) || 0
      setFormData(prev => ({ ...prev, [name]: brandIdValue }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  // Obsługa zmiany formatu pliku
  const handleFileFormatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = e.target

    setFormData(prev => ({
      ...prev,
      fileFormats: checked
        ? [...prev.fileFormats, value]
        : prev.fileFormats.filter(format => format !== value)
    }))
  }

  // Obsługa wyboru kategorii
  const handleMainCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value
    setSelectedMainCategory(category)
    setSelectedSubCategory('')
    setSelectedThirdCategory('')
    updateFormCategory(category, '', '')
  }

  const handleSubCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value
    setSelectedSubCategory(category)
    setSelectedThirdCategory('')
    updateFormCategory(selectedMainCategory, category, '')
  }

  const handleThirdCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value
    setSelectedThirdCategory(category)
    updateFormCategory(selectedMainCategory, selectedSubCategory, category)
  }

  // Aktualizacja kategorii w formularzu
  const updateFormCategory = (main: string, sub: string, third: string) => {
    let categoryPath = main
    if (sub) {
      categoryPath += `/${sub}`
      if (third) {
        categoryPath += `/${third}`
      }
    }

    setFormData(prev => ({
      ...prev,
      category: categoryPath
    }))
  }

  // Obsługa wyboru obrazka
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if ((e.target.files != null) && e.target.files.length > 0) {
      const file = e.target.files[0]
      setSelectedFile(file)

      // Tworzenie podglądu
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Obsługa wysyłania formularza
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Walidacja formularza
    if (formData.name === '' || formData.brandId === 0 || !formData.category) {
      showToast('Wypełnij wszystkie wymagane pola')
      return
    }

    if (formData.fileFormats.length === 0) {
      showToast('Wybierz co najmniej jeden format pliku')
      return
    }

    setLoading(true)

    // Przygotowanie danych do wysłania
    const dataToSend = {
      ...formData,
      priority: Number(formData.priority),
      brandId: Number(formData.brandId)
    }

    try {
      // Upload obrazka
      const imageUrl = await handleImageUpload()
      if (imageUrl) {
        dataToSend.image = imageUrl
      }

      // Zapisanie produktu
      if (isEditMode && id) {
        await requests.updateProduct(parseInt(id), dataToSend)
        showToast('Produkt został zaktualizowany')
      } else {
        await requests.createProduct(dataToSend)
        showToast('Nowy produkt został dodany')
      }

      navigate(`/brands/${formData.brandId}`)
    } catch (error) {
      console.error('Błąd podczas zapisywania produktu:', error)
      showToast('Wystąpił błąd podczas zapisywania produktu')
      setLoading(false)
    }
  }

  // Upload obrazka
  const handleImageUpload = async () => {
    if (selectedFile == null) return null

    try {
      // Przekazujemy plik i typ "other" zgodnie z wymaganiami API
      const response = await requests.uploadFile(selectedFile, 'other')
      return response.data.fileKey
    } catch (error) {
      console.error('Błąd podczas przesyłania obrazka:', error)
      showToast('Nie udało się przesłać obrazka')
      return null
    }
  }

  // Obsługa plików do pobrania
  const handleFilesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return

    const newFiles = Array.from(e.target.files)
    setFiles(prevFiles => [...prevFiles, ...newFiles])
    setFilesUploading(true)

    try {
      const uploadPromises = newFiles.map(async (file) => {
        const response = await requests.uploadFile(file, 'other')
        return response.data.fileKey
      })

      const fileKeys = await Promise.all(uploadPromises)
      setFormData(prev => ({
        ...prev,
        files: [...(prev.files|| []), ...fileKeys]
      }))
    } catch (error) {
      console.error('Błąd podczas przesyłania plików:', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się przesłać plików. Spróbuj ponownie.'
        })
      )
    } finally {
      setFilesUploading(false)
    }
  }

  const handleRemoveFile = (index: number) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index))
    setFormData(prev => ({
      ...prev,
      files: (prev.files || []).filter((_, i) => i !== index)
    }))
  }

  return {
    formData,
    loading,
    brands,
    selectedFile,
    previewUrl,
    filesUploading,
    mainCategories,
    subCategories,
    thirdLevelCategories,
    selectedMainCategory,
    selectedSubCategory,
    selectedThirdCategory,
    fileFormatOptions,
    handleChange,
    handleFileFormatChange,
    handleMainCategoryChange,
    handleSubCategoryChange,
    handleThirdCategoryChange,
    handleFileChange,
    handleFilesUpload,
    handleRemoveFile,
    handleSubmit,
    isEditMode,
    navigate,
    files
  }
}
